import { useIntl } from 'react-intl';
import * as z from 'zod';

import { MappedForm } from '@/components/elements/form-map/form-map.component';
import {
  CreateProductInput,
  CreateProductMutation,
  LifecycleStatus,
  ProductGroup,
  Template,
  UpdateProductInput,
} from '@/gql/graphql';
import { useProductGroups } from '@/hooks/use-product-groups/use-product-groups.hook';
import { useTemplates } from '@/hooks/use-templates/use-templates.hook';
import { useToast } from '@/hooks/use-toast.hook';
import { allProductGroups } from '@/pages/product-groups/product-group.requests';
import { allTemplates } from '@/pages/templates/templates.requests';
import { useDialogStore } from '@/store/dialog.store';
import { FetchResult, useMutation, useQuery } from '@apollo/client';
import { createProduct, getProductGroup, updateProduct } from '../../../products.requests';
import { toastTranslations } from '../../products.messages';
import { productMap } from './product-form.config';
import { productFormSchema } from './product-form.validation';

export type ProductFormInitialValues = {
  id: string;
  name: string;
  description: string;
  lifecycleStatus: LifecycleStatus;
  version: string;
  productIdentifier: string;
  projectIdentifier: string;
  copyAnswersFrom: string;
  groupId: string;
};

type ProductFormProps = {
  context: 'product' | 'template';
  edit?: boolean;
  initialValues?: Partial<ProductFormInitialValues>;
  handleSubmit?: (values: CreateProductInput | UpdateProductInput) => void;
  afterAdd?: (result: FetchResult<CreateProductMutation>) => void;
};

export const ProductForm = ({ initialValues, edit, context, afterAdd }: ProductFormProps) => {
  const intl = useIntl();
  const { toast } = useToast();

  const { data: templateResponse } = useQuery<{ templates: Template[] }>(allTemplates);
  const { data: productGroupResponse } = useQuery<{ productGroups: ProductGroup[] }>(allProductGroups);

  const { productGroups } = useProductGroups({
    initialProductGroups: productGroupResponse?.productGroups,
  });

  const { templates } = useTemplates({
    initialTemplates: templateResponse?.templates,
  });

  const { handleDialogClose } = useDialogStore((state) => ({
    handleDialogClose: state.closeDialog,
  }));

  const [createProductMutation, { loading: addProductLoading }] = useMutation(createProduct);

  const [editProductMutation, { loading: editProductLoading }] = useMutation(updateProduct);

  async function handleSubmit(values: z.infer<ReturnType<typeof productFormSchema>>) {
    if (edit) {
      if (!initialValues) return;
      await editProductMutation({
        variables: {
          updateProductInput: {
            id: initialValues.id || '',
            name: values.name,
            version: values.version,
            description: values.description,
            lifecycleStatus: initialValues.lifecycleStatus || LifecycleStatus.New,
            productIdentifier: values.productIdentifier || '',
            projectIdentifier: values.projectIdentifier || '',
            groupId: values.groupId || '',
          },
        },
        refetchQueries: [
          { query: allProductGroups },
          { query: getProductGroup, variables: { id: initialValues?.groupId } },
        ],
      });
      handleDialogClose();
      toast({
        description: intl.formatMessage(toastTranslations.editProductToast),
        variant: 'default',
      });
      return;
    }

    const refetchQueries =
      context === 'product'
        ? [{ query: allProductGroups }, { query: getProductGroup, variables: { id: initialValues?.groupId } }]
        : [{ query: allProductGroups }];
    const data = await createProductMutation({
      variables: {
        createProductInput: {
          name: values.name,
          description: values.description,
          version: values.version,
          groupId: initialValues?.groupId || values.groupId || '',
          productIdentifier: values.productIdentifier || '',
          projectIdentifier: values.projectIdentifier || '',
          copyAnswersFrom: values.copyAnswersFrom || undefined,
        },
      },
      refetchQueries: refetchQueries,
    });
    handleDialogClose();
    toast({
      description: intl.formatMessage(toastTranslations.addProductToast),
      variant: 'default',
    });
    if (afterAdd) afterAdd(data);
  }

  return (
    <MappedForm
      context={context}
      loading={addProductLoading || editProductLoading}
      formMap={productMap(context, intl, templates, edit, productGroups)}
      defaultValues={{
        name: '',
        description: '',
        productType: '',
        version: '',
        productIdentifier: '',
        projectIdentifier: '',
        groupId: initialValues?.groupId || undefined,
      }}
      initialValues={initialValues}
      onClose={handleDialogClose}
      onSubmit={handleSubmit}
      validationSchema={productFormSchema}
    />
  );
};
